import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Archive, RotateCcw, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { AssigneeAvatars } from "@/components/AssigneeAvatars";
import { Header } from "@/components/Header";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { rememberSpace, resolveDefaultSpace } from "@/lib/activeSpace";
import { archiveReasonLabel } from "@/lib/archiveReason";
import { type ArchivedTask, unarchiveTask } from "@/lib/board";
import { canDecide } from "@/lib/perms";
import {
  archivedInfiniteOptions,
  invalidateAfterArchiveChange,
  spacesQueryOptions,
  useUser,
} from "@/lib/queries";
import { taskModalSearch } from "@/lib/taskModalSearch";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";

const route = getRouteApi("/archived");

function archivedDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ArchivedRow({
  task,
  canRestore,
  onOpen,
  onRestore,
  restoring,
}: {
  task: ArchivedTask;
  canRestore: boolean;
  onOpen: () => void;
  onRestore: () => void;
  restoring: boolean;
}) {
  const date = archivedDate(task.statusChangedAt);
  const reason = archiveReasonLabel(task.archiveReason);
  const meta = [task.clickupListName, date && `${reason} ${date}`]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="flex cursor-pointer items-center gap-3 border-b border-border/60 px-4 py-3 transition-colors last:border-0 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <AssigneeAvatars assignees={task.assignees} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {task.title ?? "Untitled task"}
        </p>
        {meta && (
          <p className="truncate text-xs text-muted-foreground">{meta}</p>
        )}
      </div>

      {canRestore && (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 cursor-pointer gap-1.5"
          onClick={(e) => {
            e.stopPropagation();
            onRestore();
          }}
          disabled={restoring}
        >
          <RotateCcw className="size-3.5" />
          Restore
        </Button>
      )}
    </div>
  );
}

export function Archived() {
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canRestore = canDecide(user);
  // Clicking a row opens it in the shared task modal (URL-driven, like the board).
  const { task: openTaskId, space_id: spaceParam } = route.useSearch();

  // Active space mirrors the board: URL-driven, seeded from the remembered
  // choice (or the first space) when the URL doesn't name one.
  const spaceId = spaceParam ?? "";
  const { data: spaces = [] } = useQuery(spacesQueryOptions);
  useEffect(() => {
    if (spaceParam) {
      rememberSpace(spaceParam);
      return;
    }
    const target = resolveDefaultSpace(spaces);
    if (target)
      void navigate({
        to: "/archived",
        search: (prev) => ({ ...prev, space_id: target }),
        replace: true,
      });
  }, [spaceParam, spaces, navigate]);

  // Debounced title search → query key.
  const [input, setInput] = useState("");
  const search = useDebouncedValue(input.trim(), 300);

  const {
    data,
    isPending,
    isError,
    fetchStatus,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(archivedInfiniteOptions(search, spaceId));
  // Idle-but-pending = no space resolved yet; don't spin, show the empty state.
  const loading = isPending && fetchStatus !== "idle";

  const tasks = data?.pages.flatMap((p) => p.tasks) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  const restore = useMutation({
    mutationFn: (id: string) => unarchiveTask(id),
    onSuccess: (_r, id) => {
      toast.success("Task restored to review", {
        icon: <RotateCcw className="size-4" />,
      });
      invalidateAfterArchiveChange(queryClient, id);
    },
    onError: () => toast.error("Couldn't restore the task"),
  });

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Archive className="size-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold tracking-tight">Archived</h1>
          {total > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
          <div className="relative ml-auto w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Search archived…"
              className="h-9 pl-8 pr-8"
            />
            {input && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setInput("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        ) : isError ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-8 text-center text-sm text-destructive">
            Couldn't load archived tasks
          </p>
        ) : tasks.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
            {search ? "No archived tasks match your search." : "Nothing archived."}
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            {tasks.map((task) => (
              <ArchivedRow
                key={task.id}
                task={task}
                canRestore={canRestore}
                restoring={restore.isPending}
                onOpen={() =>
                  void navigate({
                    to: "/archived",
                    search: (prev) => taskModalSearch(prev, task.id),
                  })
                }
                onRestore={() => restore.mutate(task.id)}
              />
            ))}
            {hasNextPage && <div ref={sentinelRef} className="h-1" />}
          </div>
        )}
      </main>

      <TaskDetailDialog
        taskId={openTaskId ?? null}
        canDecide={canRestore}
        onOpenChange={(open) => {
          if (!open)
            void navigate({
              to: "/archived",
              search: (prev) => taskModalSearch(prev, null),
            });
        }}
      />
    </div>
  );
}

export default Archived;
